/**
 * ClaimsLogic - Service functions for Claims (identity verification state)
 */

import type { Claims, ClaimsLevel, VerificationExpiration } from './types';
import { CLAIMS_LEVEL } from './types';
import { formatTimeUntilExpiration } from './expiration';

export const ClaimsLogic = {
  create(): Claims {
    return {};
  },

  isPhoneVerified(claims: Claims): boolean {
    const now = Math.floor(Date.now() / 1000);
    return claims.phoneVerifExp !== undefined && claims.phoneVerifExp > now;
  },

  isEmailVerified(claims: Claims): boolean {
    const now = Math.floor(Date.now() / 1000);
    return claims.emailVerifExp !== undefined && claims.emailVerifExp > now;
  },

  /**
   * True when the phone was verified at some point but that verification has
   * since lapsed. Distinct from never having been verified (no expiration set),
   * which callers should surface as "unverified" rather than "expired".
   */
  isPhoneExpired(claims: Claims): boolean {
    const now = Math.floor(Date.now() / 1000);
    return claims.phoneVerifExp !== undefined && claims.phoneVerifExp <= now;
  },

  /**
   * True when the email was verified at some point but that verification has
   * since lapsed. Distinct from never having been verified (no expiration set),
   * which callers should surface as "unverified" rather than "expired".
   */
  isEmailExpired(claims: Claims): boolean {
    const now = Math.floor(Date.now() / 1000);
    return claims.emailVerifExp !== undefined && claims.emailVerifExp <= now;
  },

  isPhoneOrEmailVerified(claims: Claims): boolean {
    return (
      ClaimsLogic.isPhoneVerified(claims) || ClaimsLogic.isEmailVerified(claims)
    );
  },

  /**
   * Live migration proof — correct security answers given while
   * email-verified (see Claims.migrationProofExp). Mirrors the backend's IsMigrationProofValid.
   */
  isMigrationProofValid(claims: Claims): boolean {
    const now = Math.floor(Date.now() / 1000);
    return (
      claims.migrationProofExp !== undefined && claims.migrationProofExp > now
    );
  },

  /**
   * Mirrors the backend's phone-migration gate (PostNewIDVVerificationHandler):
   * re-pointing a profile onto a new number requires a live PHONE claim or a
   * live migration proof (an email claim alone, fresh or stale, does not
   * suffice).
   */
  mayMigratePhone(claims: Claims): boolean {
    return (
      ClaimsLogic.isPhoneVerified(claims) ||
      ClaimsLogic.isMigrationProofValid(claims)
    );
  },

  isPhoneAndEmailVerified(claims: Claims): boolean {
    return (
      ClaimsLogic.isPhoneVerified(claims) && ClaimsLogic.isEmailVerified(claims)
    );
  },

  isUnverified(claims: Claims): boolean {
    return (
      !ClaimsLogic.isPhoneVerified(claims) &&
      !ClaimsLogic.isEmailVerified(claims)
    );
  },

  earliestExpiration(claims: Claims): Date | undefined {
    const now = Math.floor(Date.now() / 1000);
    let earliest: number | undefined;

    if (claims.phoneVerifExp !== undefined && claims.phoneVerifExp > now) {
      earliest = claims.phoneVerifExp;
    }
    if (claims.emailVerifExp !== undefined && claims.emailVerifExp > now) {
      if (earliest === undefined || claims.emailVerifExp < earliest) {
        earliest = claims.emailVerifExp;
      }
    }

    return earliest !== undefined ? new Date(earliest * 1000) : undefined;
  },

  /**
   * Which verification to renew before the session's earliest claim lapses.
   * Email only when a live phone claim outlasts it: without a live phone, an
   * email confirm is briefly relaxed on the backend and grants just a few
   * minutes, so renewing by email would only bring the same prompt back.
   */
  verificationToRenew(
    claims: Claims
  ): typeof CLAIMS_LEVEL.PHONE | typeof CLAIMS_LEVEL.EMAIL {
    const { phoneVerifExp, emailVerifExp } = claims;
    if (
      ClaimsLogic.isPhoneVerified(claims) &&
      ClaimsLogic.isEmailVerified(claims) &&
      emailVerifExp !== undefined &&
      phoneVerifExp !== undefined &&
      emailVerifExp < phoneVerifExp
    ) {
      return CLAIMS_LEVEL.EMAIL;
    }
    return CLAIMS_LEVEL.PHONE;
  },

  asClaimsLevel(claims: Claims): ClaimsLevel {
    if (ClaimsLogic.isPhoneAndEmailVerified(claims)) {
      return CLAIMS_LEVEL.PHONE_AND_EMAIL;
    } else if (ClaimsLogic.isEmailVerified(claims)) {
      return CLAIMS_LEVEL.EMAIL;
    } else if (ClaimsLogic.isPhoneVerified(claims)) {
      return CLAIMS_LEVEL.PHONE;
    }
    return CLAIMS_LEVEL.UNVERIFIED;
  },

  satisfies(claims: Claims, requirement: ClaimsLevel | string): boolean {
    switch (requirement) {
      case CLAIMS_LEVEL.PHONE:
        return ClaimsLogic.isPhoneVerified(claims);
      case CLAIMS_LEVEL.EMAIL:
        return ClaimsLogic.isEmailVerified(claims);
      case CLAIMS_LEVEL.PHONE_OR_EMAIL:
        return ClaimsLogic.isPhoneOrEmailVerified(claims);
      case CLAIMS_LEVEL.PHONE_AND_EMAIL:
        return ClaimsLogic.isPhoneAndEmailVerified(claims);
      case CLAIMS_LEVEL.UNVERIFIED:
        return true;
      case '':
        return true;
      default:
        return false;
    }
  },

  withPhoneVerifExp(claims: Claims, exp: Date | undefined): Claims {
    return {
      phoneVerifExp: exp ? Math.floor(exp.getTime() / 1000) : undefined,
      emailVerifExp: claims.emailVerifExp,
      migrationProofExp: claims.migrationProofExp,
    };
  },

  withEmailVerifExp(claims: Claims, exp: Date | undefined): Claims {
    return {
      phoneVerifExp: claims.phoneVerifExp,
      emailVerifExp: exp ? Math.floor(exp.getTime() / 1000) : undefined,
      migrationProofExp: claims.migrationProofExp,
    };
  },

  toString(claims: Claims): string {
    return ClaimsLogic.asClaimsLevel(claims);
  },

  /**
   * Get phone verification expiration as a Date plus a human-friendly
   * formatted string, or undefined if not set.
   */
  phoneVerifExpiration(claims: Claims): VerificationExpiration | undefined {
    if (!claims.phoneVerifExp) return undefined;
    const date = new Date(claims.phoneVerifExp * 1000);
    return { date, formattedDate: formatTimeUntilExpiration(date) };
  },

  /**
   * Get email verification expiration as a Date plus a human-friendly
   * formatted string, or undefined if not set.
   */
  emailVerifExpiration(claims: Claims): VerificationExpiration | undefined {
    if (!claims.emailVerifExp) return undefined;
    const date = new Date(claims.emailVerifExp * 1000);
    return { date, formattedDate: formatTimeUntilExpiration(date) };
  },

  /** Compare verification state of two claims (same effective level) */
  hasSameVerifications(a: Claims, b: Claims): boolean {
    return ClaimsLogic.asClaimsLevel(a) === ClaimsLogic.asClaimsLevel(b);
  },
};
